/**
 * AGI SuperSting .stg quality control: pure functions, no DOM / React.
 *
 * Ported from Advanced-Geosciences-Inc/STG-QC (`uploads/stg_qc_app.jsx`: parseSTG,
 * classify, reasons, exportClean). The React UI is NOT ported. Everything here is
 * reachable from the worker pool (lib/geo-tasks.js task `stgQc`), so it must stay
 * free of window / document and return structured-clone friendly data.
 *
 * The backend mirrors these numbers in `backend/qc/survey_qc.py`
 * (`STG_QC_THRESHOLDS`). Change them in BOTH places (see docs/BUILDER_GUIDE.md,
 * "Resistivity QC").
 *
 * .stg layout: 3 header lines, then one CSV line per reading:
 *   rec, USER, date, time, V/I (ohm), repeat error (tenths of %), current (mA),
 *   apparent resistivity, flag text, then 12 coordinates (x,y,z for A, B, M, N).
 */

export const STG_QC_THRESHOLDS = Object.freeze({
  /** Effective error (max of repeat, reciprocal) tier limits in %: A <= 2, B <= 3, C <= 5, D above. */
  tiers: Object.freeze({ A: 2, B: 3, C: 5 }),
  /** Land signal floors: V (mV) and I (mA) under these are noise-dominated. */
  vFloorMv: 0.1,
  iFloorMa: 10,
  /** Remote electrode: |x - centroid of other three| > remoteSpanFactor * span of those three. */
  remoteSpanFactor: 5,
  /** Remote A or B is only valid when AB >= remoteAbOverAm * AM (otherwise > 5 % geometric error). */
  remoteAbOverAm: 5,
  /** Removal budget (AGI guidance): <= 5 % per pass, < 20 % total. */
  budgetPerPassPct: 5,
  budgetTotalPct: 20,
  /** Negative apparent resistivity: 'flag' (C), 'reject' (D) or 'ignore'. */
  negMode: 'flag',
  /** EarthImager-style filter: low voltage rejects (D) instead of flagging (C). */
  eiFilter: true,
});

const TIER_ORDER = ['A', 'B', 'C', 'D'];

function numberOrNull(s) {
  const v = parseFloat(s);
  return Number.isNaN(v) ? null : v;
}

/**
 * Parse .stg text.
 * @returns {{ error?: string, header: string[], sn: string, instrument: string, firmware: string,
 *   period: string, unit: string, claimed: number, parsed: number, hasIP: boolean,
 *   readings: object[], electrodes: {x:number, count:number, remote:boolean}[], recipPairs: number }}
 */
export function parseStg(text) {
  const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 4) {
    return { error: 'File too short to be a valid .stg', header: [], readings: [], electrodes: [], parsed: 0, claimed: 0, recipPairs: 0 };
  }
  const [h1, h2, h3] = lines;
  const sn = (h1.match(/S\/N:\s*(\S+)/) || [])[1] || '';
  const instrument = /SuperSting/i.test(h1) ? 'SuperSting' : /STING R1/i.test(h1) ? 'Sting R1' : 'Unknown';
  const firmware = (h2.match(/version:\s*(\S+)/i) || [])[1] || '';
  const period = (h2.match(/Survey period:\s*(\S+)/i) || [])[1] || '';
  const claimed = parseInt((h2.match(/Records:\s*(\d+)/i) || [])[1] || '0', 10);
  const unit = (h3.match(/Unit:\s*(\S+)/i) || [])[1] || 'm';

  const readings = [];
  let hasIP = false;
  for (let i = 3; i < lines.length; i++) {
    const raw = lines[i];
    const parts = raw.split(',').map((p) => p.trim());
    if (parts.length < 8 || !/^\d+$/.test(parts[0])) continue;
    const vi = parseFloat(parts[4]);
    const errT = parseInt(parts[5], 10);
    const cur = parseFloat(parts[6]);
    const rho = parseFloat(parts[7]);
    if ([vi, errT, cur, rho].some((v) => Number.isNaN(v))) continue;
    const coords = [];
    let ip = false;
    for (let j = 9; j < parts.length; j++) {
      const p = parts[j];
      if (p.startsWith('IP:')) { ip = true; break; }
      if (p.includes('=')) break;
      const v = numberOrNull(p);
      if (v === null) break;
      coords.push(v);
    }
    if (ip) hasIP = true;
    let xs = null;
    if (coords.length === 8 || coords.length === 12) {
      const s = coords.length / 4;
      xs = [coords[0], coords[s], coords[2 * s], coords[3 * s]];
    }
    readings.push({
      rec: parts[0], vi, errPct: errT / 10, cur, rho, xs, ip,
      vmV: Math.abs(vi * cur), raw, recipErr: null, pairRow: null,
    });
  }

  const recipPairs = pairReciprocals(readings);
  const { remoteXs } = detectRemoteElectrode(readings);
  const electrodes = collectElectrodes(readings, remoteXs);
  return {
    header: [h1, h2, h3], sn, instrument, firmware, period, unit, claimed,
    parsed: readings.length, hasIP, readings, electrodes, recipPairs,
  };
}

function collectElectrodes(readings, remoteXs) {
  const m = new Map();
  readings.forEach((r) => {
    if (!r.xs) return;
    r.xs.forEach((x) => m.set(x, (m.get(x) || 0) + 1));
  });
  return [...m.entries()].sort((a, b) => a[0] - b[0])
    .map(([x, count]) => ({ x, count, remote: remoteXs.includes(x) }));
}

/**
 * Match reciprocal pairs by electrode geometry (never by record number) and set
 * `recipErr` (%) and `pairRow` (1-based index) on both members. Mutates; returns pair count.
 */
export function pairReciprocals(readings) {
  let pairs = 0;
  const seen = new Map();
  readings.forEach((r, i) => {
    if (!r.xs) return;
    const [A, B, M, N] = r.xs;
    for (const v of [[M, N, A, B], [N, M, B, A], [M, N, B, A], [N, M, A, B]]) {
      const k = v.join('|');
      if (seen.has(k)) {
        const j = seen.get(k);
        const f = readings[j];
        const e = (Math.abs(Math.abs(f.vi) - Math.abs(r.vi)) / Math.abs(f.vi)) * 100;
        f.recipErr = e; r.recipErr = e; f.pairRow = i + 1; r.pairRow = j + 1;
        pairs++;
        break;
      }
    }
    seen.set([A, B, M, N].join('|'), i);
  });
  return pairs;
}

/**
 * Remote (infinity) electrode detection, per quadripole. Sets `remote`, `remoteIdx`
 * (0..3 = A,B,M,N) and `infViolation` (remote A/B with AB < 5*AM) on each reading.
 * Mutates the readings; returns `{ readings, remoteXs, count, violations }`.
 */
export function detectRemoteElectrode(readings, th = STG_QC_THRESHOLDS) {
  const remotes = new Set();
  let violations = 0;
  readings.forEach((r) => {
    r.remote = false; r.remoteIdx = -1; r.infViolation = false;
    if (!r.xs) return;
    for (let k = 0; k < 4; k++) {
      const others = r.xs.filter((_, j) => j !== k);
      const span = Math.max(...others) - Math.min(...others) || 1;
      const cen = others.reduce((a, b) => a + b, 0) / 3;
      if (Math.abs(r.xs[k] - cen) > th.remoteSpanFactor * span) {
        r.remote = true; r.remoteIdx = k; remotes.add(r.xs[k]);
        break;
      }
    }
    if (r.remote && r.remoteIdx <= 1) {
      const [A, B, M] = r.xs;
      const AB = Math.abs(A - B);
      const near = r.remoteIdx === 0 ? B : A;
      const AM = Math.abs(near - M) || 1;
      if (AB < th.remoteAbOverAm * AM) { r.infViolation = true; violations++; }
    }
  });
  return { readings, remoteXs: [...remotes], count: remotes.size, violations };
}

/**
 * Signal-floor checks (land: V >= vFloorMv, I >= iFloorMa; marine relaxes both).
 * @returns {{ rec:string, lowV:boolean, lowI:boolean }[]} one entry per reading
 */
export function signalFloorFlags(readings, opts = {}) {
  const { marine = false, vFloorMv = STG_QC_THRESHOLDS.vFloorMv, iFloorMa = STG_QC_THRESHOLDS.iFloorMa } = opts;
  return readings.map((r) => ({
    rec: r.rec,
    lowV: !marine && r.vmV < vFloorMv,
    lowI: !marine && r.cur < iFloorMa,
  }));
}

function negHint(r, vFloorMv, iFloorMa) {
  const s = [];
  if (r.vmV < vFloorMv) s.push(`V<${vFloorMv}mV, noise flipping polarity`);
  if (r.cur < iFloorMa) s.push(`I<${iFloorMa}mA, auto-ranged low (high contact R or short)`);
  if (!s.length) s.push('check cable leakage / grounded infrastructure / electrode addresses');
  return s.join('; ');
}

/**
 * Tier each reading A-D on max(repeat, reciprocal) error, signal floors, negative rho and
 * remote-electrode geometry. Returns NEW reading objects with `tier`, `effErr` and
 * `reasons` (string[]); inputs are not modified beyond remote-electrode annotation.
 *
 * @param {object[]} readings
 * @param {{A:number,B:number,C:number}} [thresholds] error limits in %
 * @param {{marine?:boolean, negMode?:string, eiFilter?:boolean, vFloorMv?:number, iFloorMa?:number}} [opts]
 */
export function tierReadings(readings, thresholds = STG_QC_THRESHOLDS.tiers, opts = {}) {
  const {
    marine = false, negMode = STG_QC_THRESHOLDS.negMode, eiFilter = STG_QC_THRESHOLDS.eiFilter,
    vFloorMv = STG_QC_THRESHOLDS.vFloorMv, iFloorMa = STG_QC_THRESHOLDS.iFloorMa,
  } = opts;
  if (readings.some((r) => r.remote === undefined)) detectRemoteElectrode(readings);
  const floors = signalFloorFlags(readings, { marine, vFloorMv, iFloorMa });
  return readings.map((r, i) => {
    const { lowV, lowI } = floors[i];
    const neg = r.rho < 0;
    const eff = Math.max(r.errPct, r.recipErr || 0);
    const negReject = neg && negMode === 'reject';
    const negFlag = neg && negMode === 'flag';
    let tier;
    if (eff > thresholds.C || negReject || (lowV && eiFilter)) tier = 'D';
    else if (eff > thresholds.B || negFlag || (lowV && !eiFilter) || lowI || r.infViolation) tier = 'C';
    else if (eff > thresholds.A) tier = 'B';
    else tier = 'A';

    const reasons = [];
    if (r.recipErr != null && r.recipErr > thresholds.B) reasons.push(`reciprocal error ${r.recipErr.toFixed(1)}% vs pair row ${r.pairRow}`);
    if (r.errPct > thresholds.C) reasons.push('high repeat error');
    else if (r.errPct > thresholds.B) reasons.push('elevated repeat error');
    if (lowV) reasons.push(`V=${r.vmV.toFixed(3)}mV < ${vFloorMv}mV floor`);
    if (lowI) reasons.push(`I=${r.cur}mA < ${iFloorMa}mA floor`);
    if (r.infViolation) reasons.push('remote electrode AB<5xAM (>5% geom. error)');
    if (neg) reasons.push(`negative rho_a: ${negHint(r, vFloorMv, iFloorMa)}`);
    return { ...r, tier, effErr: eff, reasons };
  });
}

/**
 * Summarise tiered readings: counts per tier, % kept, removal-budget verdict, worst offenders.
 * @param {object[]} readings output of tierReadings
 * @param {{dropTiers?: string[], worst?: number}} [opts]
 */
export function qcSummary(readings, opts = {}) {
  const { dropTiers = ['D'], worst = 10 } = opts;
  const counts = { A: 0, B: 0, C: 0, D: 0 };
  readings.forEach((r) => { if (counts[r.tier] !== undefined) counts[r.tier]++; });
  const total = readings.length;
  const dropped = dropTiers.reduce((s, t) => s + (counts[t] || 0), 0);
  const pctKept = total ? ((total - dropped) / total) * 100 : 0;
  const pctD = total ? (counts.D / total) * 100 : 0;
  const { budgetPerPassPct, budgetTotalPct } = STG_QC_THRESHOLDS;
  const budget = pctD > budgetTotalPct ? 'over' : pctD > budgetPerPassPct ? 'caution' : 'ok';
  const remoteXs = [...new Set(readings.filter((r) => r.remote && r.xs).map((r) => r.xs[r.remoteIdx]))];
  const recipPairs = readings.filter((r) => r.recipErr != null).length / 2;
  const worstOffenders = readings
    .filter((r) => r.tier === 'D' || r.tier === 'C')
    .sort((a, b) => TIER_ORDER.indexOf(b.tier) - TIER_ORDER.indexOf(a.tier) || b.effErr - a.effErr)
    .slice(0, worst)
    .map((r) => ({ rec: r.rec, tier: r.tier, effErr: r.effErr, reasons: r.reasons }));
  const warnings = [];
  if (budget === 'over') warnings.push(`${pctD.toFixed(1)}% of readings are tier D (over the ${budgetTotalPct}% ceiling): re-shoot rather than remove`);
  else if (budget === 'caution') warnings.push(`${pctD.toFixed(1)}% of readings are tier D (above the ${budgetPerPassPct}% per-pass guideline)`);
  if (remoteXs.length) warnings.push(`remote electrode detected at x=${remoteXs.join(', ')}`);
  const violations = readings.filter((r) => r.infViolation).length;
  if (violations) warnings.push(`${violations} remote-electrode reading(s) with AB<5xAM`);
  return { total, counts, dropped, pctKept, pctD, budget, remote: { detected: remoteXs.length > 0, xs: remoteXs, violations }, recipPairs, worstOffenders, warnings };
}

/**
 * Serialise a cleaned .stg: original header and the original raw lines of the readings whose
 * tier is not in `dropTiers` (default ['D']). The raw file is never modified.
 */
export function serializeStg(header, readings, { dropTiers = ['D'] } = {}) {
  const kept = readings.filter((r) => !dropTiers.includes(r.tier)).map((r) => r.raw);
  return [...header, ...kept].join('\n') + '\n';
}

/** Parse, tier and summarise in one call (the worker task entry point). */
export function runStgQc(text, { thresholds = STG_QC_THRESHOLDS.tiers, opts = {}, dropTiers = ['D'] } = {}, emit = () => {}) {
  emit(0.1, 'Parsing .stg');
  const parsed = parseStg(text);
  if (parsed.error) throw new Error(parsed.error);
  emit(0.5, 'Tiering readings');
  const readings = tierReadings(parsed.readings, thresholds, opts);
  emit(0.8, 'Summarising');
  const summary = qcSummary(readings, { dropTiers });
  const cleaned = {
    D: serializeStg(parsed.header, readings, { dropTiers: ['D'] }),
    CD: serializeStg(parsed.header, readings, { dropTiers: ['C', 'D'] }),
  };
  return {
    header: parsed.header, sn: parsed.sn, instrument: parsed.instrument, unit: parsed.unit,
    claimed: parsed.claimed, parsed: parsed.parsed, hasIP: parsed.hasIP,
    electrodes: parsed.electrodes, summary,
    tiers: readings.map((r) => r.tier),
    keptD: readings.filter((r) => r.tier !== 'D').length,
    keptCD: readings.filter((r) => r.tier === 'A' || r.tier === 'B').length,
    cleaned,
  };
}
